import { inject, Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** What to render, plus whatever the caller needs handed back on click. */
export interface DesktopNotificationRequest {
  title: string;
  body: string;
  icon?: string;
  /**
   * Notifications sharing a tag replace one another instead of stacking, so a
   * busy thread leaves one entry in the tray rather than one per comment.
   */
  tag?: string;
  /** Returned by clicks$ untouched; this service never interprets it. */
  data?: Record<string, string>;
}

/**
 * Tray notifications through the browser's Notification API, for reaching
 * someone whose session is open on a tab they are not looking at.
 *
 * Permission is the whole difficulty here. It can only be asked for once —
 * a refusal is permanent until the user clears it in browser settings — and
 * Firefox and Safari only raise the prompt from a user gesture, so the ask is
 * deferred to the next click rather than fired on load.
 */
@Injectable({
  providedIn: 'root',
})
export class DesktopNotificationService {
  private readonly zone = inject(NgZone);

  private readonly clicked = new Subject<Record<string, string>>();

  private asking = false;

  /** The API exists but is inert outside a secure context, so both are checked. */
  get isSupported(): boolean {
    return typeof Notification !== 'undefined' && window.isSecureContext;
  }

  get permission(): NotificationPermission {
    return this.isSupported ? Notification.permission : 'denied';
  }

  /** The `data` of whichever notification the user clicked, in the Angular zone. */
  get clicks$(): Observable<Record<string, string>> {
    return this.clicked.asObservable();
  }

  /**
   * Asks on the next click anywhere in the document, once. A dismissed prompt
   * leaves permission at 'default' and is deliberately not asked again: the
   * browser only offers one prompt, and re-asking every click cannot recover it.
   */
  requestPermissionOnFirstGesture(): void {
    if (!this.isSupported || this.permission !== 'default' || this.asking) {
      return;
    }

    this.asking = true;

    this.zone.runOutsideAngular(() => {
      document.addEventListener('click', () => void this.requestPermission(), {
        once: true,
      });
    });
  }

  /**
   * Safari below 16 only has the callback form and returns undefined here, so
   * the result is normalised back to whatever the browser settled on.
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      return 'denied';
    }

    try {
      await Notification.requestPermission();
    } catch {
      // A browser that rejects the ask is one that will not show notifications.
    }

    return this.permission;
  }

  /** No-ops unless permission has actually been granted. */
  show(request: DesktopNotificationRequest): void {
    if (this.permission !== 'granted') {
      return;
    }

    // The constructor throws on browsers that only support notifications from a
    // service worker, and its handlers fire outside Angular's zone.
    this.zone.runOutsideAngular(() => {
      try {
        const notification = new Notification(request.title, {
          body: request.body,
          icon: request.icon,
          tag: request.tag,
        });

        notification.onclick = () => {
          // The tab raising the notification is by definition not the focused
          // one, so clicking has to bring it forward itself.
          window.focus();
          notification.close();

          this.zone.run(() => this.clicked.next(request.data ?? {}));
        };
      } catch {
        // Nothing to fall back to — the in-app toast has already been shown.
      }
    });
  }
}
